/**
 * ARCHITECTURE: "on-device resize to 2048 px on the long edge, JPEG quality
 * about 0.8". The photo is drawn upright (its EXIF orientation applied) into a
 * canvas no larger than that, and re-encoded: the new file carries no EXIF,
 * so no GPS position leaves the iPad. The server strips metadata again
 * (strip.ts) and refuses what is not a whole JPEG.
 *
 * iOS refuses canvases above 16.7 megapixels and iPhones shoot 24: drawing
 * straight into the small canvas never makes a big one. One photo at a time,
 * and the canvas is emptied after: iOS frees its memory only then.
 */
export const PHOTO_LONG_EDGE = 2048
export const PHOTO_QUALITY = 0.8

/** The photo as a JPEG ready to send, or null when this browser cannot read it (HEIC on a desktop). */
export async function photoToJpeg(file: Blob): Promise<Blob | null> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return null
  }
  const canvas = document.createElement('canvas')
  try {
    const scale = Math.min(1, PHOTO_LONG_EDGE / Math.max(bitmap.width, bitmap.height))
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY),
    )
  } finally {
    bitmap.close()
    canvas.width = 0
    canvas.height = 0
  }
}
